import type { ApolloCache } from '@apollo/client';
import {
  type GetQBankStructureLevelItemsByQBankStructureIdQuery,
  type QBankStructureFragment,
  GetQBankStructureByIdDocument,
  GetQBankStructureLevelItemsByQBankStructureIdDocument,
  QBankStructureStatus,
  useUnpublishQBankStructureMutation,
  useUpdateQBankStructureAsArchivedMutation,
  useUpdateQBankStructureAsDraftMutation,
  useUpdateQBankStructureAsPublishedMutation,
  useUpdateQBankStructureAsUnpublishedMutation,
} from '@exam-management/graphql';
import { useQBankStructure, type Tag } from '@exam-management/qbs-provider';
import { Dialog, DialogTitle, Divider } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';

import QBankStructureDetailEditForm, {
  type QBankStructureDetailEditFormInput,
} from '../qbank-structure-detail-edit-form/qbank-structure-detail-edit-form';

function updateCacheOfQBankStructureAndFirstLevelItem(
  cache: ApolloCache<unknown>,
  updatedQBankStructureInformation?: QBankStructureFragment,
) {
  if (!updatedQBankStructureInformation) return;

  const cachedQBankStructureLevelItemsResult =
    cache.readQuery<GetQBankStructureLevelItemsByQBankStructureIdQuery>({
      query: GetQBankStructureLevelItemsByQBankStructureIdDocument,
      variables: {
        qBankStructureId: updatedQBankStructureInformation?.id,
        options: null,
      },
    });

  if (!cachedQBankStructureLevelItemsResult) return;

  const cachedQBankStructureLevelItems = [
    ...cachedQBankStructureLevelItemsResult.qbsGetQBankStructureLevelItemsByQBankStructureId,
  ];

  const rootLevelItemIndex = cachedQBankStructureLevelItems.findIndex(
    (qBankStructureLevelItem) => qBankStructureLevelItem.parentLevelItemId === null,
  );

  if (rootLevelItemIndex === -1) return;

  const rootLevelItem = cachedQBankStructureLevelItems[rootLevelItemIndex];

  const { tag } = rootLevelItem;

  // Cast type as Tag as it is not possible for root level item's tag to be null
  const updatedTag = { ...tag, name: updatedQBankStructureInformation?.name } as Tag;

  cachedQBankStructureLevelItems[rootLevelItemIndex] = {
    ...rootLevelItem,
    tag: updatedTag,
  };

  cache.writeQuery({
    query: GetQBankStructureLevelItemsByQBankStructureIdDocument,
    variables: {
      id: updatedQBankStructureInformation?.id,
      options: null,
    },
    data: {
      qbsGetQBankStructureLevelItemsByQBankStructureId: cachedQBankStructureLevelItems,
    },
  });

  cache.writeQuery({
    query: GetQBankStructureByIdDocument,
    variables: {
      id: updatedQBankStructureInformation?.id,
    },
    data: {
      qbsGetQBankStructureById: updatedQBankStructureInformation,
    },
  });
}

const UPDATE_QBS_SUCCESS_MESSAGE = 'QBank Structure Information has been saved';
const UPDATE_QBS_ERROR_MESSAGE = 'Unable to Save QBank Structure';

interface QBankStructureDetailEditModalProps {
  open: boolean;
  onClose: () => void;
  status: QBankStructureStatus;
}

export default function QBankStructureDetailEditModal(
  props: Readonly<QBankStructureDetailEditModalProps>,
) {
  const { open: isOpen, onClose, status } = props;
  const { qBankStructureInformation } = useQBankStructure();

  const { showSnackbar } = useSnackbar();
  const [updateAsDraft, { loading: isUpdateAsDraftLoading }] =
    useUpdateQBankStructureAsDraftMutation({
      onCompleted: () => {
        onClose();
        showSnackbar({
          message: UPDATE_QBS_SUCCESS_MESSAGE,
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: UPDATE_QBS_ERROR_MESSAGE,
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQBankStructureByIdDocument,
          variables: {
            id: qBankStructureInformation.id,
          },
        },
      ],
    });

  const [updateAsPublished, { loading: isUpdateAsPublishedLoading }] =
    useUpdateQBankStructureAsPublishedMutation({
      update: (cache, { data }) => {
        onClose();
        updateCacheOfQBankStructureAndFirstLevelItem(
          cache,
          data?.qbsUpdateQBankStructureAsPublished,
        );
      },
      onCompleted: async () => {
        onClose();
        showSnackbar({
          message: UPDATE_QBS_SUCCESS_MESSAGE,
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: UPDATE_QBS_ERROR_MESSAGE,
          variant: 'error',
        });
      },
    });

  const [updateAsUnpublished, { loading: isUpdateAsUnpublishedLoading }] =
    useUpdateQBankStructureAsUnpublishedMutation({
      update: (cache, { data }) => {
        onClose();
        updateCacheOfQBankStructureAndFirstLevelItem(
          cache,
          data?.qbsUpdateQBankStructureAsUnpublished,
        );
      },
      onCompleted: () => {
        showSnackbar({
          message: UPDATE_QBS_SUCCESS_MESSAGE,
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: UPDATE_QBS_ERROR_MESSAGE,
          variant: 'error',
        });
      },
    });

  const [updateAsArchived, { loading: isUpdateAsArchivedLoading }] =
    useUpdateQBankStructureAsArchivedMutation({
      update: (cache, { data }) => {
        onClose();
        updateCacheOfQBankStructureAndFirstLevelItem(
          cache,
          data?.qbsUpdateQBankStructureAsArchived,
        );
      },
      onCompleted: () => {
        showSnackbar({
          message: UPDATE_QBS_SUCCESS_MESSAGE,
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: UPDATE_QBS_ERROR_MESSAGE,
          variant: 'error',
        });
      },
    });

  const [unpublishQBankStructure, { loading: isUnpublishLoading }] =
    useUnpublishQBankStructureMutation({
      onCompleted: () => {
        showSnackbar({
          message: 'ปิดปรับปรุงหลักสูตรแล้ว',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'แก้ไขหลักสูตรไม่สำเร็จ',
          variant: 'error',
        });
      },
    });

  const handleSubmit = async (formValues: QBankStructureDetailEditFormInput) => {
    const { name, description, remark, type, id, group } = formValues;
    const groupTag = group ? { name: group.value as string } : null;

    if (
      formValues.status === QBankStructureStatus.Unpublished &&
      status === QBankStructureStatus.Published
    ) {
      await unpublishQBankStructure({
        variables: {
          unpublishQBankStructureId: formValues.id,
        },
      });
    }

    if (formValues.status === QBankStructureStatus.Draft) {
      await updateAsDraft({
        variables: {
          input: {
            id,
            name,
            description,
            remark,
            type,
            groupTag,
          },
        },
      });
    }

    if (formValues.status === QBankStructureStatus.Published) {
      await updateAsPublished({
        variables: {
          input: {
            id,
            description,
            remark,
            groupTag,
          },
        },
      });
    }

    if (formValues.status === QBankStructureStatus.Unpublished) {
      await updateAsUnpublished({
        variables: {
          input: {
            id,
            name,
            description,
            remark,
            groupTag,
          },
        },
      });
    }

    if (formValues.status === QBankStructureStatus.Archived) {
      await updateAsArchived({
        variables: {
          input: {
            id,
            name,
            description,
            remark,
            groupTag,
          },
        },
      });
    }
  };

  const isLoading =
    isUpdateAsDraftLoading ||
    isUpdateAsPublishedLoading ||
    isUpdateAsUnpublishedLoading ||
    isUpdateAsArchivedLoading ||
    isUnpublishLoading;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle onClose={onClose}>Edit Information</DialogTitle>
      <Divider />
      <QBankStructureDetailEditForm
        onClose={onClose}
        onSubmit={handleSubmit}
        status={status}
        loading={isLoading}
      />
    </Dialog>
  );
}
